import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Crown, Star, Zap } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/base/buttons'
import { ScoreBar } from '@/components/common'
import { useVoices } from '@/hooks/api'
import { useAudioPreview } from '@/hooks/useAudioPreview'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import { getProviderLabel, translateMetaValue } from '@/lib/voiceDisplay'
import type { Voice } from '@/types/assistant'
import { PremiumRequestModal } from './PremiumRequestModal'
import { VoiceAvatar } from './VoicePicker'
import {
  assistantLanguageSet,
  nativeStatus,
  isPremiumLocked,
  voiceCoversLanguages,
} from './voiceEligibility'

interface SelectedVoiceCardProps {
  voiceId?: number | null
  assistantId?: number
  defaultLanguage?: string
  additionalLanguages?: string[]
  /** Text the preview speaks; when omitted the backend picks a localized sample. */
  greeting?: string
}

/**
 * Detail card for the assistant's currently selected voice, shown next to the
 * voice picker. Sourced from the FULL voice list (same query the picker uses,
 * so it hits the cache): the selection must stay visible even when it no
 * longer covers the assistant's languages — that shows a warning instead of
 * hiding the user's own selection.
 */
export function SelectedVoiceCard({
  voiceId,
  assistantId,
  defaultLanguage = 'en-US',
  additionalLanguages,
  greeting,
}: SelectedVoiceCardProps) {
  const { t, i18n } = useTranslation()
  const { data: voices = [] } = useVoices({ assistant_id: assistantId })
  const voice = useMemo(() => voices.find((v) => v.id === voiceId) ?? null, [voices, voiceId])

  const { playingId, loadingId, toggle } = useAudioPreview({
    errorMessage: t('resources.assistants.voice_drawer.preview_failed', {
      defaultValue: 'Could not play the voice preview.',
    }),
  })

  const assistantLanguages = useMemo(
    () => assistantLanguageSet(defaultLanguage, additionalLanguages),
    [defaultLanguage, additionalLanguages]
  )

  const coversLanguages = useMemo(
    () => (voice ? voiceCoversLanguages(voice, assistantLanguages) : true),
    [voice, assistantLanguages]
  )

  // Premium request flow, mirrored from the picker rows: the selection must
  // show its plan lock before Save fails on it.
  const [requestTarget, setRequestTarget] = useState<Voice | null>(null)
  const [requested, setRequested] = useState(false)

  if (!voice) return null

  const flags = (voice.meta?.languages ?? []).slice(0, 3)
  const extraLangs = Math.max(0, (voice.meta?.languages?.length ?? 0) - flags.length)
  const languageNames = (voice.meta?.languages ?? [])
    .map((lang) => getLanguageDisplayName(lang, i18n.language) ?? lang)
    .join(', ')
  const isPremium = voice.meta?.pricing?.tier === 'premium'
  const isRealtime = voice.provider?.provider_type?.toLowerCase() === 'gemini'
  const bylineParts = [
    voice.meta?.gender && translateMetaValue(t, 'gender', voice.meta.gender),
    voice.meta?.age_group && translateMetaValue(t, 'age_group', voice.meta.age_group),
    voice.meta?.tones?.[0] && translateMetaValue(t, 'tone', voice.meta.tones[0]),
    getProviderLabel(voice),
  ].filter(Boolean) as string[]
  const scores = voice.meta?.scores
  const notScoredLabel = t('resources.assistants.voice_drawer.not_scored', {
    defaultValue: 'Not scored',
  })

  return (
    <div className="group/tile border-brand-200 bg-brand-50/40 dark:border-brand-800 dark:bg-brand-900/20 rounded-xl border">
      {/* Mirrors the picker's row layout (avatar, flex-1 text, w-28 score
          columns, w-32 action slot) so this card reads as one more row. */}
      <div className="flex items-center gap-4 px-5 py-3.5">
        <VoiceAvatar
          voice={voice}
          size="sm"
          isPlaying={playingId === voice.id}
          isLoading={loadingId === voice.id}
          onPlay={() =>
            void toggle(voice.id, {
              text: greeting,
              language: defaultLanguage,
              id: assistantId || 0,
              voice_id: voice.id,
            })
          }
          playPreviewLabel={t('resources.assistants.voice_drawer.play_preview_named', {
            name: voice.display_name ?? '',
            defaultValue: 'Play preview of {{name}}',
          })}
          playingPreviewLabel={t('resources.assistants.voice_drawer.stop_preview_named', {
            name: voice.display_name ?? '',
          })}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {voice.display_name}
            </span>
            {voice.meta?.suggested === true && (
              <span
                role="img"
                title={t('resources.assistants.voice_drawer.recommended_voice', {
                  defaultValue: 'Recommended voice',
                })}
                aria-label={t('resources.assistants.voice_drawer.recommended_voice', {
                  defaultValue: 'Recommended voice',
                })}
              >
                <Star
                  className="text-brand-500 dark:text-brand-400 h-3.5 w-3.5"
                  fill="currentColor"
                  aria-hidden="true"
                />
              </span>
            )}
            {flags.length > 0 && (
              <span
                role="img"
                title={languageNames}
                aria-label={languageNames}
                className="flex items-center gap-0.5 text-sm leading-none"
              >
                {flags.map((lang) => (
                  <span key={lang} aria-hidden="true">
                    {getLanguageFlag(lang)}
                  </span>
                ))}
                {extraLangs > 0 && (
                  <span className="ml-0.5 text-xs text-gray-400" aria-hidden="true">
                    +{extraLangs}
                  </span>
                )}
              </span>
            )}
            {nativeStatus(voice, defaultLanguage) === 'native' && (
              <Badge variant="success" size="sm">
                {t('resources.assistants.voice_drawer.native')}
              </Badge>
            )}
            {isRealtime && (
              <Badge variant="warning" size="sm" className="gap-0.5">
                <Zap className="h-3 w-3" />
                {t('resources.assistants.voice_drawer.realtime')}
              </Badge>
            )}
            {isPremium && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <Crown className="h-3 w-3" aria-hidden="true" />
                {t('resources.assistants.voice_drawer.premium_plan')}
              </span>
            )}
          </div>

          {(bylineParts.length > 0 || voice.description) && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {bylineParts.join(' · ')}
              {voice.description && (
                <span className="italic">
                  {bylineParts.length > 0 ? ' — ' : ''}
                  {voice.description}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Score columns on the same fixed widths as the picker rows */}
        <div className="hidden shrink-0 items-center gap-4 xl:flex">
          <div className="w-28" title={t('resources.assistants.voice_drawer.naturalness')}>
            <ScoreBar
              score={scores?.naturalness}
              label={t('resources.assistants.voice_drawer.naturalness')}
              notScoredLabel={notScoredLabel}
            />
          </div>
          <div className="w-28" title={t('resources.assistants.voice_drawer.response_speed')}>
            <ScoreBar
              score={scores?.speed}
              label={t('resources.assistants.voice_drawer.response_speed')}
              notScoredLabel={notScoredLabel}
            />
          </div>
        </div>

        {/* Occupies the picker rows' button slot, keeping columns aligned */}
        <span className="text-brand-600 dark:text-brand-400 w-32 shrink-0 text-center text-[10px] font-semibold tracking-wider uppercase">
          {t('resources.assistants.voice_drawer.selected')}
        </span>
      </div>

      {nativeStatus(voice, defaultLanguage) === 'accent' && coversLanguages && (
        <p className="px-5 pb-3 text-xs text-gray-500 dark:text-gray-400">
          {t('resources.assistants.voice_drawer.speaks_with_accent', {
            language: getLanguageDisplayName(defaultLanguage, i18n.language) ?? defaultLanguage,
          })}
        </p>
      )}

      {!coversLanguages && (
        <p className="text-warning-600 dark:text-warning-400 px-5 pb-3 text-xs">
          {t('resources.assistants.voice_drawer.selected_language_mismatch', {
            defaultValue: "This voice doesn't support all of this assistant's languages",
          })}
        </p>
      )}

      {isPremiumLocked(voice) && (
        <div className="text-warning-600 dark:text-warning-400 flex flex-wrap items-center gap-x-3 gap-y-1 px-5 pb-3 text-xs">
          <span>{t('resources.assistants.voice_drawer.selected_premium_locked')}</span>
          {assistantId ? (
            <Button
              type="button"
              color="link-color"
              size="sm"
              onPress={() => setRequestTarget(voice)}
              isDisabled={requested}
              aria-label={t('resources.assistants.voice_drawer.request_voice_named', {
                name: voice.display_name ?? '',
              })}
            >
              {requested
                ? t('resources.assistants.voice_drawer.premium_request.requested')
                : t('resources.assistants.voice_drawer.premium_request.button')}
            </Button>
          ) : (
            <span>
              {t('resources.assistants.voice_drawer.premium_request.available_after_create')}
            </span>
          )}
        </div>
      )}

      {assistantId ? (
        <PremiumRequestModal
          voice={requestTarget}
          assistantId={assistantId}
          onClose={() => setRequestTarget(null)}
          onRequested={() => setRequested(true)}
        />
      ) : null}
    </div>
  )
}

export default SelectedVoiceCard
